import React, {Fragment, useEffect, useMemo} from "react";
import ErrorIcon from "@mui/icons-material/Error";
import {Box} from "@mui/material";
import {useTranslation} from "react-i18next";
import {connect} from "react-redux";
import Call from "../../hocs/call";
import CustomEmpty from "../custom-empty";
import WidgetViewer from "../widget-viewer";
import MultiViewer from "./multi-viewer";
import MultiViewerPlus from "./multi-viewer-plus";
import SingleViewer from "./single-viewer";
import SingleViewerPlus from "./single-viewer-plus";
import {ViewerMode} from "../../state/dataset/constants";
import {
  enableDatasetStructureFetch,
  fetchDatasetStructure,
  resetDatasetState
} from "../../state/dataset/datasetActions";
import {CATALOG_PACKAGE_TYPE_DASHBOARD, getCategoryFromCatalog, getDatasetFromCatalog} from "../../utils/catalog";

const viewerContainerStyle = {
  width: "100%",
  height: "100%",
  overflow: "auto"
};

const mapStateToProps = ({themeConfig, dataset}) => ({
  isMultiViewerTheme: themeConfig.isMultiViewer,
  isFetchStructureDisabled: dataset.commons.isFetchStructureDisabled,
  viewerMode: dataset.commons.viewerMode,
  dsDatasetId: dataset.commons.datasetId,
  dsNodeCode: dataset.commons.nodeCode,
  dsCategoryPath: dataset.commons.categoryPath,
  dsViewId: dataset.commons.viewId
});

const mapDispatchToProps = dispatch => ({
  onFetchStructureEnable: (nodeCode, categoryPath, packageId, datasetId, viewId) =>
    dispatch(enableDatasetStructureFetch(nodeCode, categoryPath, packageId, datasetId, viewId)),
  fetchStructure: ({nodeId, packageId, datasetId, viewId, isMultiViewerTheme}) =>
    dispatch(fetchDatasetStructure(nodeId, packageId, datasetId, viewId, isMultiViewerTheme)),
  onDatasetReset: () => dispatch(resetDatasetState())
});

const DatasetHandler = ({
  node,
  nodeCode,
  nodeExtras,
  catalog,
  categoryPath,
  packageId,
  packageTitle,
  packageMetadata,
  packageApproved,
  packageAttachments, // attached files used in single viewer
  viewId,

  isMultiViewerTheme,
  viewerMode,
  isFetchStructureDisabled,
  dsDatasetId,
  dsNodeCode,
  dsCategoryPath,
  dsViewId,

  onFetchStructureEnable,
  fetchStructure,
  onDatasetReset
}) => {
  const {t} = useTranslation();

  const catalogDataset = useMemo(() => getDatasetFromCatalog(packageId, catalog), [packageId, catalog]);

  const datasetId = useMemo(() => catalogDataset?.datasetId ?? "", [catalogDataset]);
  const description = useMemo(() => catalogDataset?.description ?? "", [catalogDataset]);
  const source = useMemo(() => catalogDataset?.source ?? null, [catalogDataset]);
  const lastUpdate = useMemo(() => catalogDataset?.lastUpdate ?? null, [catalogDataset]);
  const notes = useMemo(() => catalogDataset?.note ?? null, [catalogDataset]);
  const type = useMemo(() => catalogDataset?.datasetType, [catalogDataset]);
  const attachedFiles = useMemo(() => catalogDataset?.attachedFiles, [catalogDataset]); // url attached files used in multi viewer
  const metadataUrl = useMemo(() => catalogDataset?.metadataUrl ?? null, [catalogDataset]);
  const dashboardUrl = useMemo(() => catalogDataset?.dashboardUrl, [catalogDataset]);

  useEffect(() => {
    return () => {
      onDatasetReset();
    };
  }, [onDatasetReset]);

  useEffect(() => {
    if (catalogDataset && datasetId) {
      const isStateAndUrlSynced =
        nodeCode === dsNodeCode &&
        (categoryPath || []).join() === (dsCategoryPath || []).join() &&
        datasetId === dsDatasetId &&
        (viewId || "") === (dsViewId || "");

      if (!isStateAndUrlSynced) {
        onFetchStructureEnable(nodeCode, categoryPath, packageId, datasetId, viewId);
      }
    }
  });

  const isDatasetVisible =
    !!datasetId &&
    catalogDataset !== null &&
    (categoryPath.length === 0 ||
      (categoryPath.length === 1 && categoryPath[0] === "uncategorized") ||
      getCategoryFromCatalog(categoryPath, catalog) !== null);

  return (
    isDatasetVisible && (
      <Call
        cb={fetchStructure}
        cbParam={{
          nodeId: node.nodeId,
          packageId: packageId,
          datasetId: datasetId,
          viewId: viewId,
          isMultiViewerTheme: isMultiViewerTheme
        }}
        disabled={isFetchStructureDisabled || type === CATALOG_PACKAGE_TYPE_DASHBOARD}
      >
        <Fragment>
          {type === CATALOG_PACKAGE_TYPE_DASHBOARD ? (
            <WidgetViewer widgetUrl={dashboardUrl} datasetTitle={packageTitle} />
          ) : (
            <Box id="data-viewer-container" sx={viewerContainerStyle}>
              {viewerMode === ViewerMode.SingleViewer ? (
                <SingleViewer
                  nodeId={node?.nodeId}
                  nodeCode={nodeCode}
                  categoryPath={categoryPath}
                  packageId={packageId}
                  packageMetadata={packageMetadata}
                  packageApproved={packageApproved}
                  datasetId={datasetId}
                  datasetTitle={packageTitle}
                  datasetDescription={description}
                  datasetSource={source}
                  datasetLastUpdate={lastUpdate}
                  datasetNotes={notes}
                  datasetMetadataUrl={metadataUrl}
                  attachedFiles={packageAttachments}
                  viewId={viewId}
                  nodeExtras={nodeExtras}
                />
              ) : viewerMode === ViewerMode.SingleViewerPlus ? (
                <SingleViewerPlus
                  nodeId={node?.nodeId}
                  nodeCode={nodeCode}
                  categoryPath={categoryPath}
                  packageId={packageId}
                  packageMetadata={packageMetadata}
                  packageApproved={packageApproved}
                  datasetId={datasetId}
                  datasetTitle={packageTitle}
                  datasetDescription={description}
                  datasetSource={source}
                  datasetLastUpdate={lastUpdate}
                  datasetNotes={notes}
                  datasetMetadataUrl={metadataUrl}
                  attachedFiles={packageAttachments}
                  viewId={viewId}
                  nodeExtras={nodeExtras}
                />
              ) : viewerMode === ViewerMode.MultiViewer ? (
                <MultiViewer
                  nodeId={node?.nodeId}
                  nodeCode={nodeCode}
                  packageId={packageId}
                  datasetId={datasetId}
                  datasetTitle={packageTitle}
                  datasetDescription={description}
                  datasetSource={source}
                  datasetLastUpdate={lastUpdate}
                  datasetNotes={notes}
                  datasetMetadataUrl={metadataUrl}
                  attachedFiles={attachedFiles}
                  viewId={viewId}
                  nodeExtras={nodeExtras}
                />
              ) : viewerMode === ViewerMode.MultiViewerPlus ? (
                <MultiViewerPlus
                  nodeId={node?.nodeId}
                  nodeCode={nodeCode}
                  packageId={packageId}
                  datasetId={datasetId}
                  datasetTitle={packageTitle}
                  datasetDescription={description}
                  datasetSource={source}
                  datasetLastUpdate={lastUpdate}
                  datasetNotes={notes}
                  datasetMetadataUrl={metadataUrl}
                  attachedFiles={attachedFiles}
                  viewId={viewId}
                  nodeExtras={nodeExtras}
                />
              ) : viewerMode === ViewerMode.Error ? (
                <CustomEmpty text={t("scenes.dataViewer.errors.fetchStructure")} image={<ErrorIcon />} />
              ) : null}
            </Box>
          )}
        </Fragment>
      </Call>
    )
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(DatasetHandler);
